/**
 * Player Entity (Flappy Bird Style)
 * Gravity constantly pulls down - must keep jumping to stay near the regression line
 * The line is the TARGET path, not a floor
 */

import Phaser from 'phaser';
import { CONSTANTS } from '../config.js';
import { audioEngine } from '../systems/AudioEngine.js';

export class Player {
    constructor(scene, x, y) {
        this.scene = scene;

        // Create sprite (using custom image)
        this.sprite = scene.add.image(x, y, 'player');
        this.sprite.setDepth(50);

        // Base size calculation (image is 1080px, we want ~40px base)
        this.baseScale = CONSTANTS.PLAYER_BASE_SCALE;

        // Physics - custom (flappy style)
        this.x = x;
        this.y = y;
        this.velocityY = 0;

        // Tuned for flappy-style gameplay
        this.GRAVITY = CONSTANTS.PLAYER_GRAVITY;
        this.JUMP_FORCE = CONSTANTS.PLAYER_JUMP_FORCE;
        this.MAX_FALL_SPEED = CONSTANTS.PLAYER_MAX_FALL_SPEED;
        this.MAX_RISE_SPEED = CONSTANTS.PLAYER_MAX_RISE_SPEED;

        // SIZE SYSTEM - Overfitting mechanic
        this.currentScale = 1.0;     // Start at base size (simple model)
        this.maxScale = CONSTANTS.PLAYER_MAX_SCALE;
        this.baseRadius = CONSTANTS.PLAYER_BASE_RADIUS;
        this.sprite.setScale(this.baseScale * this.currentScale);

        // Reference to regression line
        this.regressionLine = null;

        // State
        this.isOnLine = false;
        this.isDead = false;

        // Input - Jump only (tap to flap)
        this.spaceKey = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
        this.upKey = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
        this.wKey = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W);

        // Click/tap to jump
        scene.input.on('pointerdown', () => this.jump());

        // Invincibility
        this.isInvincible = false;
        this.invincibilityTimer = 0;
        this.flickerTimer = 0;

        // Panic level
        this.panicLevel = 0;

        // Ghost path tracking
        this.isOnGhostPath = false;
        this.ghostPathTime = 0;
        this.GHOST_DREAD_THRESHOLD = CONSTANTS.GHOST_DREAD_THRESHOLD;

        // Visual rotation based on velocity
        this.targetRotation = 0;

        // === QUANTUM SUPERPOSITION ===
        this.quantumCharge = 0;                    // 0 to QUANTUM_CHARGE_TIME
        this.quantumReady = false;                 // Fully charged
        this.quantumCooldown = 0;                  // Cooldown timer
        this.quantumActive = false;               // Currently in quantum state (triggered)
        this.quantumGhosts = [];                   // Ghost sprites when triggered
        this.quantumGlow = null;                   // Glow effect when ready
    }

    setRegressionLine(line) {
        this.regressionLine = line;
    }

    update(delta, cameraX, scrollSpeed) {
        if (this.isDead) return;

        const dt = delta / 1000;

        // Handle jump input (can spam it)
        if (Phaser.Input.Keyboard.JustDown(this.spaceKey) ||
            Phaser.Input.Keyboard.JustDown(this.upKey) ||
            Phaser.Input.Keyboard.JustDown(this.wKey)) {
            this.jump();
        }

        // Apply gravity - SCALES WITH SIZE (heavier = more gravity)
        const gravityMultiplier = this.currentScale;  // Bigger = heavier = falls faster
        this.velocityY += this.GRAVITY * gravityMultiplier * dt;

        // Clamp velocity (also scales with size)
        const maxFall = this.MAX_FALL_SPEED * gravityMultiplier;
        this.velocityY = Math.max(this.MAX_RISE_SPEED, Math.min(this.velocityY, maxFall));

        // Apply velocity
        this.y += this.velocityY * dt;

        // Top boundary only - can't fly off top
        if (this.y < 30) {
            this.y = 30;
            this.velocityY = 0;
        }

        // NO BOTTOM BOUNDARY - fall off = death
        if (this.y > this.scene.scale.height + 50) {
            this.die();
        }

        // X position follows camera
        this.x = cameraX + this.scene.scale.width * CONSTANTS.PLAYER_X_POSITION;

        // Check if near regression line
        const lineY = this.regressionLine ? this.regressionLine.getYAtX(this.x) : 300;
        const distanceFromLine = Math.abs(this.y - lineY);
        this.isOnLine = distanceFromLine < 40; // Within tolerance

        // Update sprite
        this.sprite.x = this.x;
        this.sprite.y = this.y;

        // Update quantum glow position (use sprite position - it's the final rendered position)
        this.syncQuantumGlow();

        // Rotate sprite based on velocity (visual feedback)
        this.targetRotation = (this.velocityY / this.MAX_FALL_SPEED) * 0.5;
        this.sprite.rotation = Phaser.Math.Linear(this.sprite.rotation, this.targetRotation, 0.1);

        // Calculate panic level
        const distanceFromEdge = this.x - cameraX;
        this.panicLevel = 1 - Math.min(distanceFromEdge / 300, 1);
        this.panicLevel = Math.max(0, this.panicLevel);

        // Update invincibility
        if (this.isInvincible) {
            this.invincibilityTimer -= delta;
            if (this.invincibilityTimer <= 0) {
                this.isInvincible = false;
                this.sprite.setAlpha(1);
            } else {
                this.flickerTimer += delta;
                this.sprite.setAlpha(Math.sin(this.flickerTimer * 0.02) > 0 ? 1 : 0.3);
            }
        }

        // === UPDATE QUANTUM SUPERPOSITION ===
        this.updateQuantum(delta);
    }

    updateQuantum(delta) {
        // Handle cooldown
        if (this.quantumCooldown > 0) {
            this.quantumCooldown -= delta;
            if (this.quantumCooldown <= 0) {
                this.quantumCooldown = 0;
            }
            return;
        }

        // Charge while on line (and not already ready)
        if (this.isOnLine && !this.quantumReady) {
            this.quantumCharge += delta;

            if (this.quantumCharge >= CONSTANTS.QUANTUM_CHARGE_TIME) {
                this.quantumCharge = CONSTANTS.QUANTUM_CHARGE_TIME;
                this.quantumReady = true;
                this.onQuantumReady();
            }
        } else if (!this.isOnLine && !this.quantumReady) {
            // Slowly lose charge when off line
            this.quantumCharge = Math.max(0, this.quantumCharge - delta * 0.5);
        }
    }

    onQuantumReady() {
        // Visual feedback - glow effect
        if (!this.quantumGlow) {
            this.quantumGlow = this.scene.add.circle(this.x, this.y, 35, 0x00FFFF, 0.3);
            this.quantumGlow.setDepth(49);
        }

        // Pulse animation for glow
        this.scene.tweens.add({
            targets: this.quantumGlow,
            alpha: { from: 0.3, to: 0.6 },
            scale: { from: 1, to: 1.3 },
            duration: 500,
            yoyo: true,
            repeat: -1
        });

        // Sound effect
        audioEngine.beep(800, 0.2, 'sine', 0.2);
    }

    // Called when player would take a hit - returns true if quantum absorbed it
    tryQuantumDodge() {
        if (!this.quantumReady || this.quantumActive) return false;

        // Trigger quantum superposition!
        this.triggerQuantum();
        return true;
    }

    triggerQuantum() {
        this.quantumActive = true;
        this.quantumReady = false;
        this.quantumCharge = 0;

        // Remove glow
        if (this.quantumGlow) {
            this.quantumGlow.destroy();
            this.quantumGlow = null;
        }

        const effectDuration = CONSTANTS.QUANTUM_EFFECT_DURATION;

        // === PLAYER FLICKERING (exists/not exists) ===
        this.sprite.setTint(0x00FFFF);
        const flickerEvent = this.scene.time.addEvent({
            delay: 50,
            callback: () => {
                // Rapid flicker between visible and semi-transparent
                this.sprite.setAlpha(Math.random() > 0.3 ? 0.8 : 0.2);
            },
            repeat: Math.floor(effectDuration / 50)
        });

        // === FLOWING PARTICLES (quantum foam) ===
        const particleCount = 20;
        for (let i = 0; i < particleCount; i++) {
            this.scene.time.delayedCall(i * 25, () => {
                const angle = Math.random() * Math.PI * 2;
                const speed = 50 + Math.random() * 100;
                const particle = this.scene.add.circle(
                    this.x,
                    this.y,
                    2 + Math.random() * 4,
                    0x00FFFF,
                    0.8
                );
                particle.setDepth(47);

                // Particles flow outward and fade
                this.scene.tweens.add({
                    targets: particle,
                    x: particle.x + Math.cos(angle) * speed,
                    y: particle.y + Math.sin(angle) * speed,
                    alpha: 0,
                    scale: 0.1,
                    duration: 400 + Math.random() * 200,
                    ease: 'Quad.out',
                    onComplete: () => particle.destroy()
                });
            });
        }

        // === GHOST AFTERIMAGES (phasing in/out) ===
        const ghostCount = CONSTANTS.QUANTUM_GHOST_COUNT;
        for (let i = 0; i < ghostCount; i++) {
            const delay = i * 80;
            this.scene.time.delayedCall(delay, () => {
                const offsetX = (Math.random() - 0.5) * 60;
                const offsetY = (Math.random() - 0.5) * 60;

                const ghost = this.scene.add.image(this.x + offsetX, this.y + offsetY, 'player');
                ghost.setScale(this.baseScale * this.currentScale * (0.8 + Math.random() * 0.4));
                ghost.setAlpha(0);
                ghost.setTint(0x00FFFF);
                ghost.setDepth(48);
                ghost.setBlendMode(Phaser.BlendModes.ADD);

                // Ghost phases IN then OUT (exists briefly)
                this.scene.tweens.add({
                    targets: ghost,
                    alpha: { from: 0, to: 0.6 },
                    duration: 100,
                    yoyo: true,
                    hold: 50,
                    onComplete: () => ghost.destroy()
                });

                // Ghost drifts slightly
                this.scene.tweens.add({
                    targets: ghost,
                    x: ghost.x + offsetX * 0.5,
                    y: ghost.y + offsetY * 0.5,
                    duration: 250
                });
            });
        }

        // === WAVE RING EFFECT ===
        const ring = this.scene.add.circle(this.x, this.y, 20, 0x00FFFF, 0);
        ring.setStrokeStyle(3, 0x00FFFF, 0.8);
        ring.setDepth(46);

        this.scene.tweens.add({
            targets: ring,
            scale: 3,
            alpha: 0,
            duration: 400,
            ease: 'Quad.out',
            onComplete: () => ring.destroy()
        });

        // === SOUND EFFECT (ethereal) ===
        audioEngine.beep(1200, 0.1, 'sine', 0.2);
        audioEngine.beep(800, 0.15, 'sine', 0.15);
        this.scene.time.delayedCall(100, () => {
            audioEngine.beep(600, 0.2, 'sine', 0.1);
        });

        // === CLEANUP ===
        this.scene.time.delayedCall(effectDuration, () => {
            this.quantumActive = false;
            this.sprite.setAlpha(1);
            this.sprite.clearTint();
            flickerEvent.remove();
            // Start cooldown
            this.quantumCooldown = CONSTANTS.QUANTUM_COOLDOWN;
        });
    }

    getQuantumChargePercent() {
        if (this.quantumCooldown > 0) {
            // During cooldown, show negative progress
            return -(this.quantumCooldown / CONSTANTS.QUANTUM_COOLDOWN);
        }
        return this.quantumCharge / CONSTANTS.QUANTUM_CHARGE_TIME;
    }

    isQuantumReady() {
        return this.quantumReady;
    }

    isQuantumOnCooldown() {
        return this.quantumCooldown > 0;
    }

    // Sync quantum glow to sprite position (call after any forced position changes)
    syncQuantumGlow() {
        if (this.quantumGlow) {
            this.quantumGlow.x = this.sprite.x;
            this.quantumGlow.y = this.sprite.y;
        }
    }

    jump() {
        if (this.isDead) return;

        // Add upward force (can be called repeatedly)
        this.velocityY = this.JUMP_FORCE;

        audioEngine.playJump();

        // Quick squash animation (relative to current scale)
        const currentActualScale = this.baseScale * this.currentScale;
        this.scene.tweens.add({
            targets: this.sprite,
            scaleY: currentActualScale * 0.8,
            scaleX: currentActualScale * 1.2,
            duration: 50,
            yoyo: true,
            ease: 'Quad.out'
        });
    }

    // Check if player is on the regression line (within tolerance)
    checkLineCollision(regressionLine) {
        const lineY = regressionLine.getYAtX(this.x);
        const distance = Math.abs(this.y - lineY);
        this.isOnLine = distance < 40;
        return this.isOnLine;
    }

    // Check if on ghost path
    checkGhostPath(ghostPath) {
        this.isOnGhostPath = ghostPath.isPlayerOnGhostPath(this.x, this.y);
        return this.isOnGhostPath;
    }

    updateGhostPathTime(delta) {
        if (this.isOnGhostPath) {
            this.ghostPathTime += delta;
            return this.ghostPathTime >= this.GHOST_DREAD_THRESHOLD;
        } else {
            this.ghostPathTime = 0;
            return false;
        }
    }

    takeDamage(amount) {
        if (this.isInvincible || this.isDead) return false;

        audioEngine.playDamage();

        this.isInvincible = true;
        this.invincibilityTimer = CONSTANTS.PLAYER_INVINCIBILITY_TIME;
        this.flickerTimer = 0;

        return true;
    }

    checkDeletion(cameraX) {
        const deletionX = cameraX + 10;
        if (this.x <= deletionX) {
            this.die();
            return true;
        }
        return false;
    }

    die() {
        if (this.isDead) return;

        this.isDead = true;
        audioEngine.playDamage();

        this.scene.tweens.add({
            targets: this.sprite,
            alpha: 0,
            scaleX: 0.5,
            scaleY: 0.5,
            rotation: Math.PI,
            duration: 500,
            ease: 'Power2'
        });
    }

    getPosition() {
        return { x: this.x, y: this.y };
    }

    getPanicLevel() {
        return this.panicLevel;
    }

    // OVERFITTING SYSTEM
    grow(amount) {
        const oldScale = this.currentScale;
        this.currentScale = Math.min(this.currentScale + amount, this.maxScale);
        const finalScale = this.baseScale * this.currentScale;
        this.sprite.setScale(finalScale);

        // Visual feedback - pulse effect
        this.scene.tweens.add({
            targets: this.sprite,
            scaleX: finalScale * 1.3,
            scaleY: finalScale * 1.3,
            duration: 100,
            yoyo: true,
            ease: 'Quad.out',
            onComplete: () => {
                this.sprite.setScale(this.baseScale * this.currentScale);
            }
        });

        // Color flash to indicate growth
        this.sprite.setTint(0xFF6666);
        this.scene.time.delayedCall(200, () => {
            this.sprite.clearTint();
        });

        // Audio feedback
        audioEngine.playDamage();

        return this.currentScale !== oldScale;
    }

    getCollisionRadius() {
        return this.baseRadius * this.currentScale;
    }

    // REGULARIZATION - shrink when following the line correctly
    shrink(amount) {
        this.currentScale = Math.max(CONSTANTS.PLAYER_MIN_SCALE, this.currentScale - amount);
        this.sprite.setScale(this.baseScale * this.currentScale);
    }

    getOverfitLevel() {
        // Returns 0-1 representing how overfitted the player is
        return (this.currentScale - 1.0) / (this.maxScale - 1.0);
    }

    destroy() {
        this.sprite.destroy();
    }
}
